const ALGORITHM = {name: 'RSA-OAEP', modulusLength: 4096, publicExponent: new Uint8Array([1, 0, 1]), hash: 'SHA-256'}
const DATABASE__NAME = 'vault'
const DB_STORE = 'keys'

export const crypto = {
    generateKeyPair: async () => {
        const webCrypto = getWebCrypto()
        const keyPair = await webCrypto.subtle.generateKey(ALGORITHM, true, ['encrypt', 'decrypt'])
        const publicKey = await exportKey(keyPair.publicKey, 'public')
        const privateKey = await exportKey(keyPair.privateKey, 'private')
        return {publicKey, privateKey}
    },
    encryptPrivateKey: async (privateKeyBase64, passphrase, userId) => {
        const webCrypto = getWebCrypto()
        const aesKey = await deriveAesKey(passphrase, userId)
        const iv = webCrypto.getRandomValues(new Uint8Array(12))
        const enc = new TextEncoder()
        const encrypted = await webCrypto.subtle.encrypt({name: 'AES-GCM', iv}, aesKey, enc.encode(privateKeyBase64))
        return {
            iv: bufToBase64(iv),
            blob: bufToBase64(encrypted)
        }
    },
    decryptPrivateKey: async (blob, ivBase64, passphrase, userId) => {
        const webCrypto = getWebCrypto()
        const aesKey = await deriveAesKey(passphrase, userId)
        const decrypted = await webCrypto.subtle.decrypt(
            {name: 'AES-GCM', iv: base64ToBuf(ivBase64)},
            aesKey,
            base64ToBuf(blob)
        )
        return new TextDecoder().decode(decrypted)
    },
    encrypt: async (plaintext, recipientPublicKeyBase64) => {
        const webCrypto = getWebCrypto()
        const rsaKey = await importPublicKey(recipientPublicKeyBase64)
        const aesKey = await webCrypto.subtle.generateKey({name: 'AES-GCM', length: 256}, true, ['encrypt'])
        const iv = webCrypto.getRandomValues(new Uint8Array(12))
        const encoded = new TextEncoder().encode(plaintext)
        const [encryptedMsg, exportedAesKey] = await Promise.all([
            webCrypto.subtle.encrypt({name: 'AES-GCM', iv}, aesKey, encoded),
            webCrypto.subtle.exportKey('raw', aesKey)
        ])
        const encryptedKey = await webCrypto.subtle.encrypt({name: 'RSA-OAEP'}, rsaKey, exportedAesKey)
        return bufToBase64(new TextEncoder().encode(JSON.stringify({
            iv: bufToBase64(iv),
            key: bufToBase64(encryptedKey),
            msg: bufToBase64(encryptedMsg)
        })))
    },
    decrypt: async (ciphertextBase64, privateKey) => {
        const webCrypto = getWebCrypto()
        const rsaKey = privateKey instanceof CryptoKey ? privateKey : await importPrivateKey(privateKey)
        const {iv, key: encryptedKey, msg} = JSON.parse(new TextDecoder().decode(base64ToBuf(ciphertextBase64)))
        const decryptedAesKey = await webCrypto.subtle.decrypt({name: 'RSA-OAEP'}, rsaKey, base64ToBuf(encryptedKey))
        const aesKey = await webCrypto.subtle.importKey('raw', decryptedAesKey, 'AES-GCM', false, ['decrypt'])
        const decrypted = await webCrypto.subtle.decrypt({name: 'AES-GCM', iv: base64ToBuf(iv)}, aesKey, base64ToBuf(msg))
        return new TextDecoder().decode(decrypted)
    },
    savePrivateKey: async (userId, privateKeyBase64) => {
        const key = await importPrivateKey(privateKeyBase64)
        await dbSet(`pk_${userId}`, key)
    },
    loadPrivateKey: async userId => dbGet(`pk_${userId}`),
    clearPrivateKeys: async () => deleteDb(),
}

const getWebCrypto = () => {
    const webCrypto = window.crypto
    if (!webCrypto?.subtle) throw new Error('Web Crypto API is unavailable. Open the app in a secure context.')
    return webCrypto
}
const bufToBase64 = buf => {
    let binary = ''
    const bytes = new Uint8Array(buf)
    for (let i = 0; i < bytes.byteLength; i++) binary += String.fromCharCode(bytes[i])
    return btoa(binary)
}
const base64ToBuf = base64 => Uint8Array.from(atob(base64), c => c.charCodeAt(0))
const openDb = () => new Promise((resolve, reject) => {
    const req = indexedDB.open(DATABASE__NAME, 1)
    req.onupgradeneeded = e => {
        const db = e.target.result
        if (!db.objectStoreNames.contains(DB_STORE)) db.createObjectStore(DB_STORE)
    }
    req.onsuccess = e => resolve(e.target.result)
    req.onerror = e => reject(e.target.error)
})
const dbGet = async key => dbRequest('readonly', store => store.get(key))
const dbSet = async (key, value) => dbRequest('readwrite', store => store.put(value, key))
const deleteDb = () => new Promise((resolve, reject) => {
    const request = indexedDB.deleteDatabase(DATABASE__NAME)
    request.onsuccess = () => resolve()
    request.onerror = event => reject(event.target.error)
    request.onblocked = () => reject(new Error('Vault key storage is open in another tab.'))
})
const dbRequest = async (mode, createRequest) => {
    const db = await openDb()
    return new Promise((resolve, reject) => {
        const transaction = db.transaction(DB_STORE, mode)
        const request = createRequest(transaction.objectStore(DB_STORE))
        let result
        request.onsuccess = event => { result = event.target.result }
        transaction.oncomplete = () => {
            db.close()
            resolve(result)
        }
        transaction.onerror = event => {
            db.close()
            reject(event.target.error || transaction.error)
        }
        transaction.onabort = event => {
            db.close()
            reject(event.target.error || transaction.error)
        }
    })
}
const exportKey = async (key, type) => {
    const exported = await getWebCrypto().subtle.exportKey(type === 'public' ? 'spki' : 'pkcs8', key)
    return bufToBase64(exported)
}
const importPublicKey = async base64 =>
    getWebCrypto().subtle.importKey('spki', base64ToBuf(base64), ALGORITHM, false, ['encrypt'])
const importPrivateKey = async base64 =>
    getWebCrypto().subtle.importKey('pkcs8', base64ToBuf(base64), ALGORITHM, false, ['decrypt'])
const deriveAesKey = async (passphrase, userId) => {
    const webCrypto = getWebCrypto()
    const enc = new TextEncoder()
    const base = await webCrypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, ['deriveKey'])
    return webCrypto.subtle.deriveKey(
        {name: 'PBKDF2', salt: enc.encode(`vault:${userId}`), iterations: 310000, hash: 'SHA-256'},
        base,
        {name: 'AES-GCM', length: 256},
        false,
        ['encrypt', 'decrypt']
    )
}
